import Phaser from 'phaser';
import InputHelper from './modules/InputHelper.js';
import DebugHelper from './modules/DebugHelper.js';
import UIStyleHelper from './modules/UIStyleHelper.js';
import BackgroundHelper from './modules/BackgroundHelper.js';
import ItemDataLoader from './modules/ItemDataLoader.js';
import { deepClone } from './modules/utils/PlayerDataSync.js';
import { getResumeSceneKey } from './modules/SceneRegistry.js';
import GameStateStore from './modules/GameStateStore.js';
import SaveSystem from './modules/SaveSystem.js';

export default class CharacterSelectScene extends Phaser.Scene {
    constructor() {
        super('CharacterSelectScene');
    }
    
    init(data = {}) {
        // 标记是否来自登录场景
        this.fromLogin = data.fromLogin || false;
        
        // 角色配置（与preloader和游戏系统保持一致）
        this.characters = [
            {
                id: 1,
                name: '主角1',
                element: 'player1',
                avatarKey: 'player_avatar1',
                battleSprite: '主角1',
                status: 'p1',
                // 初始属性配置（战士型：高攻击，中等防御和速度）
                initialStats: {
                    hp: 60,
                    maxHp: 60,
                    attack: 3,
                    defense: 5,
                    speed: 30,
                    money: 5000,
                    class: '战车驾驶员'
                },
                // 初始装备配置（只写装备ID，从items.json加载完整数据）
                initialEquipment: ['pistol', 'clothRobe'],
                // 初始战车配置
                initialVehicle: {
                    name: 'tank1',
                    element: 'tank1',
                    chassisHp: 500,
                    currentChassisHp: 500,
                    weapons: {
                        '主炮': '',
                        '副炮': '',
                        '超级武器': ''
                    },
                    equipped: {
                        'chassis': '',
                        'engine': '',
                        'ai': ''
                    }
                },
                inVehicle: false,
                // 初始技能
                initialSkills: [
                    { name: '赤心计', heal: 20, cost: 10 }
                ]
            },
            {
                id: 2,
                name: '主角2',
                element: 'player2',
                avatarKey: 'player_avatar2',
                battleSprite: '主角2',
                status: 'p2',
                // 初始属性配置（敏捷型：中等攻击，低防御，高速度）
                initialStats: {
                    hp: 50,
                    maxHp: 50,
                    attack: 2,
                    defense: 4,
                    speed: 45,
                    money: 5000,
                    class: '维修专家'
                },
                // 初始装备配置（只写装备ID，从items.json加载完整数据）
                initialEquipment: ['pistol', 'clothRobe'],
                // 初始战车配置（轻量型或无战车）
                initialVehicle: {
                    name: 'tank2',
                    element: 'tank2',
                    chassisHp: 300,
                    currentChassisHp: 300,
                    weapons: {
                        '主炮': '',
                        '副炮': '',
                        '超级武器': ''
                    },
                    equipped: {
                        'chassis': '',
                        'engine': '',
                        'ai': ''
                    }
                },
                inVehicle: false,
                // 初始技能（敏捷型技能）
                initialSkills: [
                    { name: '疾风步', damage: 15, cost: 3 }
                ]
            },
            {
                id: 3,
                name: '主角3',
                element: 'player3',
                avatarKey: 'player_avatar3',
                battleSprite: '主角3',
                status: 'p3',
                // 初始属性配置（坦克型：低攻击，高防御，低速度）
                initialStats: {
                    hp: 80,
                    maxHp: 80,
                    attack: 8,
                    defense: 6,
                    speed: 60,
                    money: 10000,
                    class: '战斗专家'
                },
                // 初始装备配置（只写装备ID，从items.json加载完整数据）
                initialEquipment: ['machineGun', 'clothRobe'],
                // 初始战车配置（重型战车）
                initialVehicle: {
                    name: 'tank3',
                    element: 'tank3',
                    chassisHp: 800,
                    currentChassisHp: 800,
                    weapons: {
                        '主炮': '',
                        '副炮': '',
                        '超级武器': ''
                    },
                    equipped: {
                        'chassis': '',
                        'engine': '',
                        'ai': ''
                    }
                },
                inVehicle: false,
                // 初始技能（防御型技能）
                initialSkills: [
                    { name: '防御姿态', damage: 10, cost: 3 }
                ]
            }
        ];
        
        this.selectedCharacter = null;
        this.playerName = '';
        this.characterCards = [];
        this.nameInputDialog = null;
        this.enterButton = null;
    }
    
    create() {
        // 初始化物品数据加载器（从items.json加载数据）
        if (this.cache.json.exists('items')) {
            ItemDataLoader.initialize(this);
        } else {
            DebugHelper.warn('CharacterSelectScene: items.json 未加载，装备数据功能可能不可用');
        }
        
        // 设置背景（使用BackgroundHelper）
        BackgroundHelper.createSolidBackground(this, 0x1a1a2e);
        
        // 创建标题（使用UIStyleHelper）
        const titleText = this.fromLogin ? '选择角色（首次登录）' : '选择角色';
        UIStyleHelper.createTitle(this, titleText, 60);
        
        // 提示文字（使用UIStyleHelper）
        const hintText = this.fromLogin ? '请选择一个角色开始游戏' : '选择一个角色开始游戏';
        UIStyleHelper.createHint(this, hintText, 100);
        
        // 创建角色选择卡片
        this.createCharacterCards();
        
        // 设置键盘控制
        this.setupControls();
    }
    
    createCharacterCards() {
        const centerX = this.cameras.main.centerX;
        const startY = this.cameras.main.centerY - 50;
        const cardSpacing = 200; // 增加间距，让卡片更分散
        const cardWidth = 150;
        const cardHeight = 200;
        
        // 计算第一个卡片的位置，使三个卡片居中
        const totalWidth = (this.characters.length - 1) * cardSpacing;
        const firstCardX = centerX - totalWidth / 2;
        
        this.characters.forEach((character, index) => {
            const cardX = firstCardX + (index * cardSpacing);
            const cardY = startY;
            
            // 创建容器（先创建容器，所有元素都添加到容器中，使用相对坐标）
            const cardContainer = this.add.container(cardX, cardY);
            
            // 创建卡片背景（使用相对坐标，相对于容器中心）
            const cardBg = this.add.graphics();
            cardBg.fillStyle(0x2a2a3e, 1);
            cardBg.fillRoundedRect(
                -cardWidth / 2,
                -cardHeight / 2,
                cardWidth,
                cardHeight,
                10
            );
            cardBg.lineStyle(3, 0x4cc9f0, 0.5);
            cardBg.strokeRoundedRect(
                -cardWidth / 2,
                -cardHeight / 2,
                cardWidth,
                cardHeight,
                10
            );
            
            // 创建头像（使用相对坐标）
            let avatarImage = null;
            // 检查资源是否存在
            if (this.textures.exists(character.avatarKey)) {
                avatarImage = this.add.image(0, -40, character.avatarKey);
                avatarImage.setDisplaySize(100, 100);
            } else {
                // 如果头像资源不存在，显示占位符
                const placeholder = this.add.rectangle(0, -40, 100, 100, 0x666666);
                placeholder.setStrokeStyle(2, 0x888888);
                const placeholderText = this.add.text(
                    0,
                    -40,
                    character.id.toString(),
                    {
                        fontSize: '48px',
                        fill: '#ffffff',
                        fontFamily: 'monospace'
                    }
                );
                placeholderText.setOrigin(0.5);
                avatarImage = placeholder; // 使用占位符作为avatarImage引用
            }
            
            // 角色名称（使用相对坐标）
            const nameText = this.add.text(
                0,
                60,
                character.name,
                {
                    fontSize: '16px',
                    fill: '#ffffff',
                    fontFamily: 'monospace'
                }
            );
            nameText.setOrigin(0.5);
            
            // 将所有元素添加到容器中
            cardContainer.add([cardBg]);
            if (avatarImage) cardContainer.add(avatarImage);
            cardContainer.add(nameText);
            
            // 设置交互
            cardContainer.setSize(cardWidth, cardHeight);
            cardContainer.setInteractive({ useHandCursor: true });
            
            // 悬停效果
            cardContainer.on('pointerover', () => {
                if (!this.selectedCharacter) {
                    cardBg.clear();
                    cardBg.fillStyle(0x3a3a4e, 1);
                    cardBg.fillRoundedRect(
                        -cardWidth / 2,
                        -cardHeight / 2,
                        cardWidth,
                        cardHeight,
                        10
                    );
                    cardBg.lineStyle(3, 0x4cc9f0, 1);
                    cardBg.strokeRoundedRect(
                        -cardWidth / 2,
                        -cardHeight / 2,
                        cardWidth,
                        cardHeight,
                        10
                    );
                }
            });
            
            cardContainer.on('pointerout', () => {
                if (!this.selectedCharacter || this.selectedCharacter.id !== character.id) {
                    cardBg.clear();
                    cardBg.fillStyle(0x2a2a3e, 1);
                    cardBg.fillRoundedRect(
                        -cardWidth / 2,
                        -cardHeight / 2,
                        cardWidth,
                        cardHeight,
                        10
                    );
                    cardBg.lineStyle(3, 0x4cc9f0, 0.5);
                    cardBg.strokeRoundedRect(
                        -cardWidth / 2,
                        -cardHeight / 2,
                        cardWidth,
                        cardHeight,
                        10
                    );
                }
            });
            
            // 点击选择
            cardContainer.on('pointerdown', () => {
                this.selectCharacter(character);
            });
            
            this.characterCards.push({
                character: character,
                container: cardContainer,
                bg: cardBg,
                avatar: avatarImage,
                nameText: nameText
            });
        });
    }
    
    selectCharacter(character) {
        // 如果已经选择了角色且正在输入名字，不重复选择
        if (this.selectedCharacter && this.nameInputDialog) {
            return;
        }
        
        this.selectedCharacter = character;
        
        // 更新卡片视觉效果
        this.characterCards.forEach(card => {
            if (card.character.id === character.id) {
                // 选中状态
                card.bg.clear();
                card.bg.fillStyle(0x1a4a6e, 1);
                card.bg.fillRoundedRect(
                    -75,
                    -100,
                    150,
                    200,
                    10
                );
                card.bg.lineStyle(4, 0x4cc9f0, 1);
                card.bg.strokeRoundedRect(
                    -75,
                    -100,
                    150,
                    200,
                    10
                );
            } else {
                // 未选中状态
                card.bg.clear();
                card.bg.fillStyle(0x2a2a3e, 1);
                card.bg.fillRoundedRect(
                    -75,
                    -100,
                    150,
                    200,
                    10
                );
                card.bg.lineStyle(2, 0x4cc9f0, 0.3);
                card.bg.strokeRoundedRect(
                    -75,
                    -100,
                    150,
                    200,
                    10
                );
            }
        });
        
        // 显示输入名字对话框
        this.showNameInputDialog();
    }
    
    showNameInputDialog() {
        // 创建对话框背景
        const dialogBg = this.add.graphics();
        dialogBg.fillStyle(0x1a1a2e, 0.95);
        dialogBg.fillRoundedRect(
            this.cameras.main.centerX - 260,
            this.cameras.main.centerY - 80,
            520,
            200,
            15
        );
        dialogBg.lineStyle(3, 0x4cc9f0, 1);
        dialogBg.strokeRoundedRect(
            this.cameras.main.centerX - 260,
            this.cameras.main.centerY - 80,
            520,
            200,
            15
        );
        
        // 提示文字
        const promptText = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.centerY - 40,
            '请输入角色名称:',
            {
                fontSize: '18px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            }
        );
        promptText.setOrigin(0.5);
        
        // 创建HTML输入框
        const input = document.createElement('input');
        input.type = 'text';
        input.value = '';
        input.placeholder = '请输入角色名称';
        input.maxLength = 20;
        input.style.position = 'absolute';
        input.style.left = (this.cameras.main.centerX - 220) + 'px';
        input.style.top = (this.cameras.main.centerY - 10) + 'px';
        input.style.width = '440px';
        input.style.height = '35px';
        input.style.fontSize = '16px';
        input.style.fontFamily = 'monospace';
        input.style.backgroundColor = '#2a2a3e';
        input.style.color = '#ffffff';
        input.style.border = '2px solid #4cc9f0';
        input.style.borderRadius = '5px';
        input.style.padding = '5px 10px';
        input.style.zIndex = '10000';
        input.style.textAlign = 'center';
        document.body.appendChild(input);
        input.focus();
        
        // 确定按钮
        const confirmButton = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.centerY + 80,
            '确定',
            {
                fontSize: '18px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#4cc9f0',
                padding: { x: 30, y: 10 }
            }
        );
        confirmButton.setOrigin(0.5);
        confirmButton.setInteractive({ useHandCursor: true });
        
        const closeDialog = () => {
            const name = input.value.trim();
            if (name && name.length > 0) {
                this.playerName = name;
                document.body.removeChild(input);
                dialogBg.destroy();
                promptText.destroy();
                confirmButton.destroy();
                this.nameInputDialog = null;
                
                // 显示进入按钮
                this.showEnterButton();
            } else {
                // 如果名字为空，提示用户
                this.showMessage('请输入角色名称', '#ff0000');
            }
        };
        
        confirmButton.on('pointerdown', closeDialog);
        
        // 回车键确认
        input.addEventListener('keydown', (e) => {
            if (e.key === ' ' || e.code === 'Space') {
                e.preventDefault();
                closeDialog();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                document.body.removeChild(input);
                dialogBg.destroy();
                promptText.destroy();
                confirmButton.destroy();
                this.nameInputDialog = null;
                this.selectedCharacter = null;
                
                // 恢复卡片状态
                this.characterCards.forEach(card => {
                    card.bg.clear();
                    card.bg.fillStyle(0x2a2a3e, 1);
                    card.bg.fillRoundedRect(
                        -75,
                        -100,
                        150,
                        200,
                        10
                    );
                    card.bg.lineStyle(3, 0x4cc9f0, 0.5);
                    card.bg.strokeRoundedRect(
                        -75,
                        -100,
                        150,
                        200,
                        10
                    );
                });
            }
        });
        
        this.nameInputDialog = {
            bg: dialogBg,
            prompt: promptText,
            button: confirmButton,
            input: input
        };
    }
    
    showEnterButton() {
        // 如果已经显示过进入按钮，不重复显示
        if (this.enterButton) {
            return;
        }
        
        // 显示选中的角色信息
        const infoText = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.height - 150,
            `已选择: ${this.selectedCharacter.name} | 角色名: ${this.playerName}`,
            {
                fontSize: '16px',
                fill: '#4cc9f0',
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: { x: 15, y: 8 }
            }
        );
        infoText.setOrigin(0.5);
        
        // 创建进入按钮
        this.enterButton = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.height - 100,
            '进入游戏',
            {
                fontSize: '24px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#4cc9f0',
                padding: { x: 40, y: 15 }
            }
        );
        this.enterButton.setOrigin(0.5);
        this.enterButton.setInteractive({ useHandCursor: true });
        
        // 悬停效果
        this.enterButton.on('pointerover', () => {
            this.enterButton.setStyle({ backgroundColor: '#5cd9f0' });
        });
        
        this.enterButton.on('pointerout', () => {
            this.enterButton.setStyle({ backgroundColor: '#4cc9f0' });
        });
        
        // 点击进入游戏
        this.enterButton.on('pointerdown', () => {
            this.enterGame();
        });
        
        this.enterButtonInfo = infoText;
    }
    
    enterGame() {
        if (!this.selectedCharacter || !this.playerName) {
            this.showMessage('请完成角色选择', '#ff0000');
            return;
        }
        
        // 更新游戏状态中的玩家数据
        const player = GameStateStore.getPlayer();
        if (player) {
            const patch = {
                name: this.playerName,
                element: this.selectedCharacter.element,
                battle: this.selectedCharacter.battleSprite,
                cost: player.maxCost || 100,
                level: 1,
                exp: 0,
                nextLevelExp: 100,
                inventory: []
            };

            if (this.selectedCharacter.initialStats) {
                const stats = this.selectedCharacter.initialStats;
                Object.assign(patch, {
                    hp: stats.hp,
                    maxHp: stats.maxHp,
                    attack: stats.attack,
                    defense: stats.defense,
                    speed: stats.speed,
                    money: stats.money,
                    class: stats.class || '猎人'
                });
            }

            if (this.selectedCharacter.initialEquipment?.length > 0) {
                patch.equipment = typeof this.selectedCharacter.initialEquipment[0] === 'string'
                    ? ItemDataLoader.getItemsByIds(this.selectedCharacter.initialEquipment)
                    : deepClone(this.selectedCharacter.initialEquipment);
            } else {
                patch.equipment = [];
            }

            if (this.selectedCharacter.initialVehicle) {
                patch.vehicle = deepClone(this.selectedCharacter.initialVehicle);
                patch.inVehicle = this.selectedCharacter.inVehicle || false;
            } else {
                patch.vehicle = null;
                patch.inVehicle = false;
            }

            patch.skills = this.selectedCharacter.initialSkills?.length > 0
                ? deepClone(this.selectedCharacter.initialSkills)
                : [];

            GameStateStore.patchPlayer(patch);

            DebugHelper.log('玩家数据已更新:', {
                name: player.name,
                element: player.element,
                battle: player.battle,
                stats: {
                    hp: player.hp,
                    maxHp: player.maxHp,
                    attack: player.attack,
                    defense: player.defense,
                    speed: player.speed
                },
                equipmentCount: player.equipment.length,
                hasVehicle: !!player.vehicle,
                skillsCount: player.skills.length
            });
        }
        
        if (window.userInfo && window.userInfo.userId) {
            SaveSystem.save(0).then((success) => {
                DebugHelper.log(success ? '角色数据已保存到服务器' : '角色数据保存失败');
            });
        }
        
        // 如果来自登录场景，跳转到标题场景；否则直接进入游戏
        if (this.fromLogin) {
            DebugHelper.log('角色选择完成，跳转到标题场景');
            this.scene.start('TitleScene');
        } else {
            const targetScene = getResumeSceneKey(GameStateStore.getState());
            
            DebugHelper.log('跳转到初始场景:', targetScene);
            
            this.scene.start(targetScene);
        }
    }
    
    showMessage(message, color = '#ffffff') {
        // 清除之前的消息
        if (this.messageText) {
            this.messageText.destroy();
        }
        
        // 创建消息文本
        this.messageText = this.add.text(
            this.cameras.main.centerX,
            this.cameras.main.height - 50,
            message,
            {
                fontSize: '16px',
                fill: color,
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                padding: { x: 15, y: 8 }
            }
        );
        this.messageText.setOrigin(0.5);
        
        // 2秒后自动消失
        this.time.delayedCall(2000, () => {
            if (this.messageText) {
                this.messageText.destroy();
                this.messageText = null;
            }
        });
    }
    
    setupControls() {
        // ESC键返回
        this.escapeKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        this.escapeKey.on('down', () => {
            if (this.nameInputDialog) {
                // 如果正在输入名字，关闭输入框
                if (this.nameInputDialog.input && this.nameInputDialog.input.parentNode) {
                    document.body.removeChild(this.nameInputDialog.input);
                }
                if (this.nameInputDialog.bg) this.nameInputDialog.bg.destroy();
                if (this.nameInputDialog.prompt) this.nameInputDialog.prompt.destroy();
                if (this.nameInputDialog.button) this.nameInputDialog.button.destroy();
                this.nameInputDialog = null;
                this.selectedCharacter = null;
                
                // 恢复卡片状态
                this.characterCards.forEach(card => {
                    card.bg.clear();
                    card.bg.fillStyle(0x2a2a3e, 1);
                    card.bg.fillRoundedRect(
                        -75,
                        -100,
                        150,
                        200,
                        10
                    );
                    card.bg.lineStyle(3, 0x4cc9f0, 0.5);
                    card.bg.strokeRoundedRect(
                        -75,
                        -100,
                        150,
                        200,
                        10
                    );
                });
            } else {
                // 如果来自登录场景，不允许返回（必须选择角色）
                if (this.fromLogin) {
                    this.showMessage('请先选择角色才能继续', '#ffaa00');
                } else {
                    // 返回标题场景
                    this.scene.start('TitleScene');
                }
            }
        });
    }
    
    update() {
        // 更新场景逻辑
    }
}

